import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { T } from '../../../Translations';

interface Props<T> {
  suggestions: ReadonlyArray<T>;
  isLoading: boolean;
  onSuggestionClick: (suggestion: T) => void;
  onCreateClick?: (query: string) => void;
  inputWidth?: number;
  isActive: boolean;
  renderSuggestion: (suggestion: T, index: number, isSelected: boolean) => React.ReactNode;
  allowCreate?: boolean;
  createLabel?: string;
  query?: string;
}

function TypeaheadSuggestionsDropdown<T>({
  suggestions,
  isLoading,
  onSuggestionClick,
  onCreateClick,
  inputWidth,
  isActive,
  renderSuggestion,
  allowCreate = false,
  createLabel,
  query = '',
}: Props<T>) {
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Reset selected index when suggestions change (adjust during render)
  const [prevSuggestions, setPrevSuggestions] = useState(suggestions);
  if (suggestions !== prevSuggestions) {
    setPrevSuggestions(suggestions);
    setSelectedIndex(-1);
  }

  const showCreateOption =
    allowCreate && !isLoading && query.length >= 2 && suggestions.length === 0;

  // Handle keyboard navigation - only when dropdown is active and has suggestions
  useEffect(() => {
    if (!isActive || (suggestions.length === 0 && !showCreateOption)) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle our specific keys to avoid conflicts
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
        return;
      }

      // Only handle if the active element is related to our input
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && activeElement.tagName === 'INPUT';

      if (!isInputFocused) {
        return;
      }

      const totalItems = suggestions.length + (showCreateOption ? 1 : 0);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            onSuggestionClick(suggestions[selectedIndex]);
          } else if (selectedIndex === suggestions.length && showCreateOption && onCreateClick) {
            onCreateClick(query);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setSelectedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    suggestions,
    selectedIndex,
    onSuggestionClick,
    onCreateClick,
    isActive,
    query,
    showCreateOption,
  ]);

  return (
    <div
      className={clsx(
        'absolute z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg',
        'max-h-48 overflow-y-auto',
      )}
      style={{ width: inputWidth || '100%' }}
    >
      {isLoading && (
        <div className="px-4 py-2 text-sm text-gray-500">
          <T kind="Loading..." />
        </div>
      )}

      {!isLoading && suggestions.length === 0 && !showCreateOption && (
        <div className="px-4 py-2 text-sm text-gray-500">
          <T kind="No Results" />
        </div>
      )}

      {!isLoading &&
        suggestions.map((suggestion, index) => {
          const isSelected = selectedIndex === index;
          return (
            <button
              key={index}
              className={clsx(
                'w-full px-4 py-2 text-left text-sm border-none bg-transparent cursor-pointer',
                'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
                isSelected && 'bg-gray-100',
              )}
              style={{
                color: '#111827', // text-gray-900 equivalent
                backgroundColor: isSelected ? '#f3f4f6' : 'transparent', // bg-gray-100 equivalent
              }}
              onMouseEnter={(e) => {
                setSelectedIndex(index);
                (e.target as HTMLElement).style.backgroundColor = '#f3f4f6';
                (e.target as HTMLElement).style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                if (selectedIndex !== index) {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                }
                (e.target as HTMLElement).style.color = '#111827';
              }}
              onMouseDown={(e) => {
                // Prevent input blur when clicking
                e.preventDefault();
                onSuggestionClick(suggestion);
              }}
            >
              {renderSuggestion(suggestion, index, isSelected)}
            </button>
          );
        })}

      {showCreateOption && (
        <button
          className={clsx(
            'w-full px-4 py-2 text-left text-sm border-none bg-transparent cursor-pointer',
            'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
            'text-teal-600 font-medium border-t border-gray-200',
            selectedIndex === suggestions.length && 'bg-gray-100',
          )}
          style={{
            color: selectedIndex === suggestions.length ? '#111827' : '#0d9488',
            backgroundColor: selectedIndex === suggestions.length ? '#f3f4f6' : 'transparent',
          }}
          onMouseEnter={(e) => {
            setSelectedIndex(suggestions.length);
            (e.target as HTMLElement).style.backgroundColor = '#f3f4f6';
            (e.target as HTMLElement).style.color = '#111827';
          }}
          onMouseLeave={(e) => {
            if (selectedIndex !== suggestions.length) {
              (e.target as HTMLElement).style.backgroundColor = 'transparent';
            }
            (e.target as HTMLElement).style.color =
              selectedIndex === suggestions.length ? '#111827' : '#0d9488';
          }}
          onMouseDown={(e) => {
            // Prevent input blur when clicking
            e.preventDefault();
            if (onCreateClick) {
              onCreateClick(query);
            }
          }}
        >
          <span className="flex items-center gap-2">
            <span>+</span>
            <span>{createLabel || `Create "${query}"`}</span>
          </span>
        </button>
      )}
    </div>
  );
}

export default TypeaheadSuggestionsDropdown;
